import React from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Chip,
  LinearProgress,
  Stack,
  Divider,
  Button
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import {
  Person as PersonIcon,
  CheckCircle as CheckCircleIcon,
  Schedule as ScheduleIcon,
  Warning as WarningIcon,
  TrendingUp as TrendingUpIcon,
  Timer as TimerIcon,
  Download as DownloadIcon
} from '@mui/icons-material';
import { formatarPorcentagem, obterCorTaxaConclusao, formatarTempoConclusao } from '../../services/relatorioService';

interface MetricasColaboradorProps {
  colaborador: {
    id: string;
    nome: string;
    email: string;
    departamento: string;
  };
  metricas: {
    totalTarefas: number;
    tarefasConcluidas: number;
    tarefasConcluidasNoPrazo: number;
    tarefasConcluidasComAtraso: number;
    tarefasPendentes: number;
    tarefasAtrasadas: number;
    taxaConclusao: number;
    tempoMedioConclusao: number;
  };
  onExportar?: () => void;
}

const MetricasColaborador: React.FC<MetricasColaboradorProps> = ({ colaborador, metricas, onExportar }) => {
  const handleExportar = () => {
    if (onExportar) {
      onExportar();
    }
  };
  return (
    <Card sx={{ mb: 3, border: '2px solid #1976d2', boxShadow: 3 }}>
      <CardContent>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <PersonIcon sx={{ fontSize: 40, color: 'primary.main', mr: 2 }} />
            <Box>
              <Typography variant="h5" sx={{ fontWeight: 'bold' }}>
                Métricas do Colaborador
              </Typography>
              <Typography variant="h6" color="primary">
                {colaborador.nome}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {colaborador.departamento}
              </Typography>
            </Box>
          </Box>
          {onExportar && (
            <Button
              startIcon={<DownloadIcon />}
              onClick={handleExportar}
              variant="contained"
              sx={{
                backgroundColor: '#4caf50',
                '&:hover': {
                  backgroundColor: '#45a049'
                }
              }}
            >
              Exportar
            </Button>
          )}
        </Box>

        <Divider sx={{ mb: 3 }} />

        <Stack spacing={2}>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
            <Box sx={{ flex: 1 }}>
              <Card sx={{ bgcolor: 'action.hover', height: '100%', transition: 'transform 0.15s, box-shadow 0.15s', '&:hover': { transform: 'translateY(-2px)', boxShadow: 3 } }}>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                    <TrendingUpIcon sx={{ mr: 1, color: 'primary.main' }} />
                    <Typography variant="body2" color="text.secondary">
                      Total de Tarefas
                    </Typography>
                  </Box>
                  <Typography variant="h4" sx={{ fontWeight: 'bold' }}>
                    {metricas.totalTarefas}
                  </Typography>
                </CardContent>
              </Card>
            </Box>

            <Box sx={{ flex: 1 }}>
              <Card sx={{ bgcolor: (theme) => alpha(theme.palette.success.main, 0.12), height: '100%', transition: 'transform 0.15s, box-shadow 0.15s', '&:hover': { transform: 'translateY(-2px)', boxShadow: 3 } }}>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                    <CheckCircleIcon sx={{ mr: 1, color: 'success.main' }} />
                    <Typography variant="body2" color="text.secondary">
                      Concluídas
                    </Typography>
                  </Box>
                  <Typography variant="h4" sx={{ fontWeight: 'bold', color: 'success.main' }}>
                    {metricas.tarefasConcluidas}
                  </Typography>
                  <Stack direction="row" spacing={1} sx={{ mt: 1 }} flexWrap="wrap" useFlexGap>
                    <Chip 
                      label={`No prazo: ${metricas.tarefasConcluidasNoPrazo}`} 
                      size="small" 
                      color="success" 
                      variant="outlined"
                    />
                    {metricas.tarefasConcluidasComAtraso > 0 && (
                      <Chip 
                        label={`Atrasadas: ${metricas.tarefasConcluidasComAtraso}`} 
                        size="small" 
                        color="error" 
                        variant="outlined"
                      />
                    )}
                  </Stack>
                </CardContent>
              </Card>
            </Box>

            <Box sx={{ flex: 1 }}>
              <Card sx={{ bgcolor: (theme) => alpha(theme.palette.warning.main, 0.12), height: '100%', transition: 'transform 0.15s, box-shadow 0.15s', '&:hover': { transform: 'translateY(-2px)', boxShadow: 3 } }}>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                    <ScheduleIcon sx={{ mr: 1, color: 'warning.main' }} />
                    <Typography variant="body2" color="text.secondary">
                      Pendentes
                    </Typography>
                  </Box>
                  <Typography variant="h4" sx={{ fontWeight: 'bold', color: 'warning.main' }}>
                    {metricas.tarefasPendentes}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                    incluídas nas {metricas.totalTarefas} totais
                  </Typography>
                </CardContent>
              </Card>
            </Box>

            <Box sx={{ flex: 1 }}>
              <Card sx={{ bgcolor: (theme) => alpha(theme.palette.error.main, 0.12), height: '100%', transition: 'transform 0.15s, box-shadow 0.15s', '&:hover': { transform: 'translateY(-2px)', boxShadow: 3 } }}>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                    <WarningIcon sx={{ mr: 1, color: 'error.main' }} />
                    <Typography variant="body2" color="text.secondary">
                      Atrasadas
                    </Typography>
                  </Box>
                  <Typography variant="h4" sx={{ fontWeight: 'bold', color: 'error.main' }}>
                    {metricas.tarefasAtrasadas}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                    incluídas nas {metricas.totalTarefas} totais
                  </Typography>
                </CardContent>
              </Card>
            </Box>
          </Stack>

          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
            <Box sx={{ flex: 1 }}>
              <Card sx={{ height: '100%' }}>
                <CardContent>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    Taxa de Conclusão
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Typography 
                      variant="h3" 
                      sx={{ 
                        fontWeight: 'bold',
                        color: obterCorTaxaConclusao(metricas.taxaConclusao)
                      }}
                    >
                      {formatarPorcentagem(metricas.taxaConclusao)}
                    </Typography>
                    <Box sx={{ flex: 1 }}>
                      <LinearProgress
                        variant="determinate"
                        value={metricas.taxaConclusao}
                        sx={{
                          height: 12,
                          borderRadius: 6,
                          '& .MuiLinearProgress-bar': {
                            backgroundColor: obterCorTaxaConclusao(metricas.taxaConclusao)
                          }
                        }}
                      />
                      <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                        {metricas.tarefasConcluidas} de {metricas.totalTarefas} tarefas
                      </Typography>
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            </Box>

            <Box sx={{ flex: 1 }}>
              <Card sx={{ height: '100%' }}>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                    <TimerIcon sx={{ mr: 1, color: 'info.main' }} />
                    <Typography variant="body2" color="text.secondary">
                      Tempo Médio de Conclusão
                    </Typography>
                  </Box>
                  <Typography variant="h3" sx={{ fontWeight: 'bold', color: 'info.main' }}>
                    {formatarTempoConclusao(metricas.tempoMedioConclusao)}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Média de tempo para concluir tarefas
                  </Typography>
                </CardContent>
              </Card>
            </Box>
          </Stack>
        </Stack>

        <Box sx={{ mt: 3, p: 2, bgcolor: 'action.hover', borderRadius: 1 }}>
          <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 'bold' }}>
            Indicadores de Performance
          </Typography>
          <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap>
            {metricas.taxaConclusao >= 80 && (
              <Chip 
                label="Alta Performance" 
                color="success" 
                size="small" 
                icon={<TrendingUpIcon />}
              />
            )}
            {metricas.taxaConclusao >= 60 && metricas.taxaConclusao < 80 && (
              <Chip 
                label="Performance Média" 
                color="warning" 
                size="small"
              />
            )}
            {metricas.taxaConclusao < 60 && metricas.totalTarefas > 0 && (
              <Chip 
                label="Precisa Atenção" 
                color="error" 
                size="small" 
                icon={<WarningIcon />}
              />
            )}
            {metricas.tarefasAtrasadas > 0 && (
              <Chip 
                label={`${metricas.tarefasAtrasadas} Tarefa(s) Atrasada(s)`} 
                color="error" 
                size="small" 
                variant="outlined"
              />
            )}
            {metricas.tarefasConcluidasComAtraso > 0 && (
              <Chip 
                label={`${metricas.tarefasConcluidasComAtraso} Concluída(s) com Atraso`} 
                color="warning" 
                size="small" 
                variant="outlined"
              />
            )}
            {metricas.tempoMedioConclusao <= 3 && metricas.tarefasConcluidas > 0 && (
              <Chip 
                label="Rápido na Conclusão" 
                color="info" 
                size="small"
              />
            )}
          </Stack>
        </Box>
      </CardContent>
    </Card>
  );
};

export default MetricasColaborador;
